
/* Sidebar menu Drawer + Collapsible (Prestige → Dawn, global accordion for 1st & 2nd level)
   - Drawer: 開閉・外側クリック閉じ・ESC・オーバーレイ・フォーカストラップ・Theme Editorイベント
   - Collapsible: 1階層 & 2階層は「常にひとつだけ開く」（グローバル・アコーディオン）
   - CSSの height トランジションに完全追従（display/visibility は CSS が制御）
   - autoExpand: min-width: 641px でトグル無効（CSSと一致）
*/

(function () {
  'use strict';

  // ====== 設定・ユーティリティ ======
  const BP_AUTO = window.matchMedia('(min-width: 641px)');   // CSSの autoExpand と一致
  const isAutoExpandBreakpoint = () => BP_AUTO.matches;

  // オーバーレイ生成（なければ作る）
  function ensureOverlay() {
    let ov = document.querySelector('.PageOverlay');
    if (!ov) {
      ov = document.createElement('div');
      ov.className = 'PageOverlay';
      document.body.appendChild(ov);
    }
    return ov;
  }

  // フォーカストラップ（簡易）
  function trapFocus(container) {
    const focusable = container.querySelectorAll(
      'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last  = focusable[focusable.length - 1];

    function handler(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last)  { e.preventDefault(); first.focus(); }
      }
    }
    container.__trapFocusHandler = handler;
    container.addEventListener('keydown', handler);
  }
  function removeTrapFocus(container) {
    if (container.__trapFocusHandler) {
      container.removeEventListener('keydown', container.__trapFocusHandler);
      delete container.__trapFocusHandler;
    }
  }

  // ====== Drawer ======
  class Drawer {
    constructor(container) {
      this.element = container;
      this.pageOverlayElement = ensureOverlay();
      this.isOpen = false;
      this.prevFocused = null;
      this.activeOpener = null;

      this._attachListeners();
      this._calculateMaxHeight();
      this.close({ type: 'init' });  // 初期は閉じ
    }

    destroy() {
      document.body.removeEventListener('click', this._onBodyClick);
      this.element.removeEventListener('keyup', this._onKeyup);
      window.removeEventListener('resize', this._onResize);
      this.pageOverlayElement.removeEventListener('click', this._onOverlayClick);
      removeTrapFocus(this.element);
    }

    toggle(e) { this.isOpen ? this.close(e) : this.open(e); }

    open(e) {
      if (this.isOpen) return;
      if (e && e.preventDefault) e.preventDefault();

      // 検索閉じ（元テーマ互換）
      this.element.dispatchEvent(new CustomEvent('search:close', { bubbles: true }));

      this.prevFocused = document.activeElement;

      this.element.setAttribute('aria-hidden', 'false');
      this._calculateMaxHeight();

      document.documentElement.classList.add('no-scroll'); // スクロール抑止

      trapFocus(this.element);

      // オーバーレイ表示
      this.pageOverlayElement.classList.add('is-visible');

      this.isOpen = true;

      // フォーカス移動
      const focusTarget =
        this.element.querySelector('.Drawer__Close') ||
        this.element.querySelector('button, a, [tabindex]:not([tabindex="-1"])');
      focusTarget && focusTarget.focus();

      // 開いたボタンの aria-expanded を同期
      if (this.activeOpener) {
        this.activeOpener.setAttribute('aria-expanded', 'true');
      }

      return false;
    }

    close(e) {
      // 初期化時も通す
      if (!this.isOpen && (!e || e.type !== 'init')) {
        if (e && e.preventDefault) e.preventDefault();
        return;
      }
      if (e && e.preventDefault) e.preventDefault();

      this.element.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('no-scroll');

      removeTrapFocus(this.element);

      this.pageOverlayElement.classList.remove('is-visible');

      this.isOpen = false;

      // 開いたボタンの aria-expanded を同期
      if (this.activeOpener) {
        this.activeOpener.setAttribute('aria-expanded', 'false');
      }

      // 元のフォーカスへ戻す
      if (this.prevFocused && typeof this.prevFocused.focus === 'function') {
        this.prevFocused.focus();
      }
    }

    _attachListeners() {
      // 開閉ボタン／外側クリック（body委譲）
      this._onBodyClick = (ev) => {
        const openBtn  = ev.target.closest(`[data-action="open-drawer"][data-drawer-id="${this.element.id}"]`);
        const closeBtn = ev.target.closest(`[data-action="close-drawer"][data-drawer-id="${this.element.id}"]`);

        if (openBtn) {
          this.activeOpener = openBtn;
          this.open(ev);
          return; // 開いた直後に外側クリック判定させない
        }
        if (closeBtn) {
          this.close(ev);
          return;
        }

        // 外側クリックで閉じる（Drawer外をクリック）
        const clickedInside = this.element.contains(ev.target);
        if (!clickedInside && this.isOpen) {
          this.close(ev);
        }
      };
      document.body.addEventListener('click', this._onBodyClick);

      // オーバーレイクリックで閉じる
      this._onOverlayClick = (ev) => { this.close(ev); };
      this.pageOverlayElement.addEventListener('click', this._onOverlayClick);

      // ESCで閉じる
      this._onKeyup = (ev) => {
        if (this.isOpen && (ev.key === 'Escape' || ev.keyCode === 27)) this.close(ev);
      };
      this.element.addEventListener('keyup', this._onKeyup);

      // リサイズで高さ再計算
      this._onResize = () => this._calculateMaxHeight();
      window.addEventListener('resize', this._onResize);

      // Theme Editorイベント
      document.addEventListener('shopify:section:select', (evt) => {
        // Theme Editor: only auto-open drawer when preview is mobile (below breakpoint)
        if (evt?.target?.contains(this.element) && !isAutoExpandBreakpoint()) this.open(evt);
      });
      document.addEventListener('shopify:section:deselect', (evt) => {
        if (evt?.target?.contains(this.element)) this.close(evt);
      });
      document.addEventListener('shopify:section:unload', (evt) => {
        if (evt?.target?.contains(this.element)) this.destroy();
      });
    }

    _calculateMaxHeight() {
      this.element.style.maxHeight = window.innerHeight + 'px';
    }
  }

  // ====== Collapsible（#sidebar-menu 内限定：1階層 & 2階層は常にひとつだけ開く） ======
  class Collapsible {
    /**
     * @param {Element} root Drawer内のルート要素（例：document.querySelector('#sidebar-menu')）
     * @param {Object}  opts { transitionMs: 350 }
     */
    constructor(root, opts = {}) {
      this.root = root || document.body;
      this.opts = Object.assign({ transitionMs: 350 }, opts);

      // クリックハンドラ（キャプチャで早めに拾って伝播抑止）
      this._onClick = this._onClick.bind(this);
      this.root.addEventListener('click', this._onClick, true);

      // 初期同期：aria-expanded に応じて height を反映
      this._syncInitialHeights();
    }

    destroy() { this.root.removeEventListener('click', this._onClick, true); }

    // 初期同期：開いているものは height:auto、閉じているものは 0px
    _syncInitialHeights() {
      this.root.querySelectorAll('.Collapsible__Button[aria-expanded]').forEach((btn) => {
        const inner = btn.nextElementSibling;
        if (!inner || !inner.classList.contains('Collapsible__Inner')) return;
        const expanded = btn.getAttribute('aria-expanded') === 'true';
        inner.style.height = expanded ? 'auto' : '0px';
      });
    }

    _onClick(e) {
      const btn = e.target.closest('[data-action="toggle-collapsible"]');
      if (!btn || !this.root.contains(btn)) return;

      // Collapsible制御だけに使う
      e.preventDefault();
      e.stopPropagation();

      const collapsible = btn.closest('.Collapsible');
      const inner = collapsible?.querySelector('.Collapsible__Inner');
      if (!collapsible || !inner) return;

      // autoExpand：641px以上ではトグル無効
      if (collapsible.classList.contains('Collapsible--autoExpand') && isAutoExpandBreakpoint()) {
        return;
      }

      // アニメ中は無視
      if (inner.dataset.animating) return;

      // 階層判定：
      // 第二階層の Collapsible は「親Collapsibleの中（.Collapsible__Content）」に存在する
      const parentCollapsibleAncestor = collapsible.parentElement.closest('.Collapsible');
      const isSecondLevel = !!parentCollapsibleAncestor;
      const isFirstLevel  = !isSecondLevel;

      const isOpen = btn.getAttribute('aria-expanded') === 'true';

      if (isOpen) {
        this._closeOne(btn, inner);
      } else {
        this._openOne(btn, inner);

        // 兄弟クローズ：グローバル（Drawer全体）で「同じ階層」の他項目を閉じる
        if (isFirstLevel) {
          this._closeAllOtherFirstLevel(btn);
        } else {
          this._closeAllOtherSecondLevel(btn);
        }
      }
    }

    // ---- アニメ（CSS height 0.35s に合わせる） ----
    _openOne(btn, inner) {
      btn.setAttribute('aria-expanded', 'true');

      inner.dataset.animating = 'down';
      // 開始は 0px
      inner.style.height = '0px';
      // 次フレームでコンテンツ高さへ
      requestAnimationFrame(() => {
        inner.style.transition = `height ${this.opts.transitionMs}ms ease`;
        inner.style.height = inner.scrollHeight + 'px';
      });

      const onEnd = () => {
        inner.style.transition = '';
        inner.style.height = 'auto'; // アニメ後は auto に固定
        delete inner.dataset.animating;
        inner.removeEventListener('transitionend', onEnd);
      };
      inner.addEventListener('transitionend', onEnd);
    }

    _closeOne(btn, inner) {
      btn.setAttribute('aria-expanded', 'false');

      inner.dataset.animating = 'up';
      // 現在高さ（autoなら scrollHeight を計測）
      const current =
        inner.style.height === 'auto'
          ? inner.scrollHeight
          : parseFloat(inner.style.height) || inner.scrollHeight;

      inner.style.transition = `height ${this.opts.transitionMs}ms ease`;
      inner.style.height = current + 'px';
      // 次フレームで 0px に
      requestAnimationFrame(() => {
        inner.style.height = '0px';
      });

      const onEnd = () => {
        inner.style.transition = '';
        inner.style.height = '0px'; // 閉じ後は 0px（CSS初期と一致）
        delete inner.dataset.animating;
        inner.removeEventListener('transitionend', onEnd);
      };
      inner.addEventListener('transitionend', onEnd);
    }

    // ---- 第一階層（Drawer全体）で現在以外を全部閉じる ----
    _closeAllOtherFirstLevel(currentBtn) {
      const allFirstButtons = this.root.querySelectorAll(
        '.SidebarMenu__Nav--primary > .Collapsible > .Collapsible__Button'
      );
      allFirstButtons.forEach((btn1) => {
        if (btn1 === currentBtn) return;
        const inner1 = btn1.nextElementSibling;
        if (!inner1 || !inner1.classList.contains('Collapsible__Inner')) return;
        if (btn1.getAttribute('aria-expanded') === 'true' && !inner1.dataset.animating) {
          this._closeOne(btn1, inner1);
        }
      });

      // 第一階層を切り替えた場合、第二階層は結果的に閉じられますが、
      // 念のため Drawer 全体の第二階層も全閉にしておくとより堅牢です（必要なら下記を有効化）
      // this._closeAllOtherSecondLevel(null);
    }

    // ---- 第二階層（Drawer全体）で現在以外を全部閉じる ----
    _closeAllOtherSecondLevel(currentBtn) {
      const allSecondButtons = this.root.querySelectorAll(
        '.SidebarMenu__Nav--primary .Collapsible .Collapsible > .Collapsible__Button'
      );
      allSecondButtons.forEach((btn2) => {
        if (currentBtn && btn2 === currentBtn) return; // currentBtn がある場合は除外
        const inner2 = btn2.nextElementSibling;
        if (!inner2 || !inner2.classList.contains('Collapsible__Inner')) return;
        if (btn2.getAttribute('aria-expanded') === 'true' && !inner2.dataset.animating) {
          this._closeOne(btn2, inner2);
        }
      });
    }
  }

  // ====== 起動 ======
  document.addEventListener('DOMContentLoaded', () => {
    const drawerEl = document.querySelector('#sidebar-menu');
    if (drawerEl) {
      // Drawer
      if (!window.__sidebarMenu) {
        window.__sidebarMenu = new Drawer(drawerEl);
      }
      // Collapsible：#sidebar-menu 内に限定（1階層＆2階層は常にひとつだけ開く）
      if (!window.__sidebarCollapse) {
        window.__sidebarCollapse = new Collapsible(drawerEl, { transitionMs: 350 });
      }
    }
  });
})();
